<template>
  <Container
    id="heading"
  >
    <HeroCard
      :heading="t('jc.view.news.hero.title')"
      :paragraph="t('jc.view.news.hero.text')"
      :image="{
        src: 'images/nature/hill_evening.png',
        alt: t('jc.view.news.hero.banner.alt'),
      }"
      :float="{
        src: 'images/skin_renders/compositions/reading_news.png',
        alt: t('jc.view.news.hero.float.alt'),
      }"
      :label="t('jc.view.news.hero.button.label')"
      :icon="{ prefix: 'fab', iconName: 'discord' }"
      :to="config.public.discordUrl"
    />
  </Container>

  <Container>
    <div
      class="flex flex-col gap-8"
    >
      <!-- admin controls -->
      <auth-only team>
        <div
          class="flex flex-col laptop:flex-row gap-2"
        >
          <button
            class="bg-secondary hover:bg-secondary-highlighted active:bg-secondary-selected text-white
              text-center rounded py-2 px-4 col-start-1 flex flex-row gap-3 font-semibold"
            @click="createNews"
          >
            <font-awesome-icon
              icon="plus"
              class="text-[1.2em]"
            />
            <span>{{ t('jc.view.news.submitButton.label') }}</span>
          </button>

          <button
            class="bg-secondary hover:bg-secondary-highlighted active:bg-secondary-selected text-white
              text-center rounded py-2 px-4 col-start-1 font-semibold"
            :title="t(`jc.view.news.toggleHidden.${showHidden ? 'hide' : 'show'}.title`)"
            @click="showHidden = !showHidden"
          >
            {{ t(`jc.view.news.toggleHidden.${showHidden ? 'hide' : 'show'}.label`) }}
          </button>
        </div>
      </auth-only>

      <!-- news -->
      <PagePagination
        :page="page"
        :pending="status === 'pending'"
        :n-pages="nPages"
      />
      <NewsCardList
        v-if="news"
        landscape
        :news="news"
      />
      <Spinner
        v-else
      />
      <PagePagination
        :page="page"
        :pending="status === 'pending'"
        :n-pages="nPages"
      />
    </div>
  </Container>
</template>

<script setup lang="ts">
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { NewsType } from '~/composables/charon'
import { uid } from '~/utils/util'

definePageMeta({
  nameKey: 'news',
  canonicalQueries: ['page'],
  head: {
    imageUrl: '/images/view/news/meta_preview.png',
  },
})

const { t, locale } = useI18n()
const localePath = useLocalePath()
const route = useRoute()
const router = useRouter()
const config = useRuntimeConfig()
const charon = useCharon(true)

const pageSize = 10
const showHidden = ref(false)

const nPages = computed(() => Math.ceil((count.value || 0) / pageSize))
const page = computed(() => {
  let query = route.query['page']
  if (Array.isArray(query)) {
    query = query[0]
  }
  return clamp(parseInt(query || '1'), 1, nPages.value) - 1
})

const { data: count } = useAsyncData(
  'news-count',
  () => useCharon(showHidden.value).countNews({
    'locales[exists]': locale.value,
  }),
  {
    lazy: true,
    default: () => 0,
    watch: [showHidden, locale],
  },
)

const { status, data: news } = useAsyncData(
  'news-list',
  () => useCharon(showHidden.value).listNews({
    'page': Math.max(page.value, 0),
    'size': pageSize,
    'sort': ['draft[dec]', 'publishDate[dec]'],
    // show all languages if 'showHidden'
    'locales[exists]': showHidden.value ? undefined : locale.value,
  }),
  {
    lazy: true,
    server: false,
    default: () => [],
    watch: [showHidden, page, locale],
  },
)

function createNews() {
  charon.createNews({
    id: null,
    createDate: null,
    updateDate: null,
    slug: uid(),
    type: NewsType.BLOG,
    tags: [],
    publishDate: null,
    draft: true,
    published: null,
    locales: {},
  }).then((news) => {
    router.push(localePath({
      name: 'news-news-edit',
      params: {
        news: news.id,
      },
    }))
  })
}
</script>
